import { useEffect, useReducer, useRef } from 'react';
import { WORKSPACE_TOKENS } from '@/lib/constants';

/**
 * Tutor pane three-state machine (R1-R8 remediation — see ui-plan.md Addendum R8.2).
 *
 *   ┌─────────────┐  EXPAND  ┌─────────┐
 *   │   hidden    │◀─────────│   rail  │
 *   └─────────────┘          └─────────┘
 *         ▲                       ▲  │
 *    HIDE │                       │  │ COLLAPSE_TO_RAIL
 *         │         EXPAND        │  ▼
 *         │     ┌─────────────────┘
 *         │     │
 *         └─────┴──▶ ┌───────────┐
 *                    │ expanded  │  SET_WIDTH_PX (clamped MIN..MAX)
 *                    └───────────┘
 *
 * VIEWPORT_CHANGED transitions expanded → rail when viewport can't afford
 * CONTENT_MIN + TUTOR_MIN, and → hidden when viewport can't afford mobile
 * fallback + rail. Prevents the 30px-tutor render bug.
 */

export type TutorPaneState = 'expanded' | 'rail' | 'hidden';

export interface TutorPaneModel {
  state: TutorPaneState;
  expandedWidthPx: number;
}

export type TutorPaneAction =
  | { type: 'EXPAND' }
  | { type: 'COLLAPSE_TO_RAIL' }
  | { type: 'HIDE' }
  | { type: 'SET_WIDTH_PX'; px: number }
  | { type: 'VIEWPORT_CHANGED'; containerPx: number };

const {
  TUTOR_MIN_PX,
  TUTOR_MAX_PX,
  TUTOR_DEFAULT_PX,
  TUTOR_RAIL_WIDTH_PX,
  CONTENT_MIN_PX,
  CONTENT_MOBILE_FALLBACK_PX,
  TUTOR_PANE_WIDTH_STORAGE_KEY,
} = WORKSPACE_TOKENS;

/** Minimum container width (px) for the tutor pane to render in `expanded` state. */
export const EXPANDED_MIN_CONTAINER_PX = CONTENT_MIN_PX + TUTOR_MIN_PX; // 880
/** Minimum container width (px) for the tutor pane to render as a rail. */
export const RAIL_MIN_CONTAINER_PX = CONTENT_MOBILE_FALLBACK_PX + TUTOR_RAIL_WIDTH_PX; // 468
/** Default tutor pane width as a percentage of the SplitView container. */
export const TUTOR_DEFAULT_PCT = 30;
/** Re-export the pixel floor so consumers don't need to import WORKSPACE_TOKENS. */
export const TUTOR_MIN_PX_EXPORT = TUTOR_MIN_PX;

/**
 * Compute the tutor pane's minimum size as a percentage of the given container
 * width (px), such that the pane never renders below TUTOR_MIN_PX (320). Capped
 * at 50 so it can't exceed the SplitView's maxSize and trigger a range error.
 */
export function computeTutorMinPct(containerPx: number): number {
  const safe = Math.max(containerPx, 1);
  return Math.min(50, Math.ceil((TUTOR_MIN_PX / safe) * 100));
}

const clampWidth = (px: number): number =>
  Math.max(TUTOR_MIN_PX, Math.min(TUTOR_MAX_PX, px));

export const initialTutorPaneModel: TutorPaneModel = {
  state: 'expanded',
  expandedWidthPx: TUTOR_DEFAULT_PX,
};

export function tutorPaneReducer(
  model: TutorPaneModel,
  action: TutorPaneAction,
): TutorPaneModel {
  switch (action.type) {
    case 'EXPAND':
      return model.state === 'expanded' ? model : { ...model, state: 'expanded' };

    case 'COLLAPSE_TO_RAIL':
      return model.state === 'rail' ? model : { ...model, state: 'rail' };

    case 'HIDE':
      return model.state === 'hidden' ? model : { ...model, state: 'hidden' };

    case 'SET_WIDTH_PX': {
      if (model.state !== 'expanded') return model;
      const next = clampWidth(action.px);
      return next === model.expandedWidthPx ? model : { ...model, expandedWidthPx: next };
    }

    case 'VIEWPORT_CHANGED': {
      const { containerPx } = action;
      if (containerPx < RAIL_MIN_CONTAINER_PX) {
        return model.state === 'hidden' ? model : { ...model, state: 'hidden' };
      }
      if (containerPx < EXPANDED_MIN_CONTAINER_PX && model.state === 'expanded') {
        return { ...model, state: 'rail' };
      }
      return model;
    }

    default:
      return model;
  }
}

function readPersistedWidth(): number {
  if (typeof window === 'undefined') return TUTOR_DEFAULT_PX;
  const raw = window.localStorage.getItem(TUTOR_PANE_WIDTH_STORAGE_KEY);
  if (!raw) return TUTOR_DEFAULT_PX;
  const n = Number.parseInt(raw, 10);
  return Number.isFinite(n) ? clampWidth(n) : TUTOR_DEFAULT_PX;
}

function persistWidth(px: number): void {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(TUTOR_PANE_WIDTH_STORAGE_KEY, String(px));
}

export interface UseTutorPaneStateOptions {
  /** Optional override for the initial state. Use when the consumer already
   *  persists an open/closed boolean in a legacy storage key (e.g.
   *  iskool:lesson-panels:tutor) and wants the hook to honor it on mount. */
  initialState?: TutorPaneState;
}

export function useTutorPaneState(
  options: UseTutorPaneStateOptions = {},
): [TutorPaneModel, React.Dispatch<TutorPaneAction>] {
  const { initialState = 'expanded' } = options;
  const [model, dispatch] = useReducer(
    tutorPaneReducer,
    null,
    (): TutorPaneModel => ({
      state: initialState,
      expandedWidthPx: readPersistedWidth(),
    }),
  );

  const lastPersistedRef = useRef(model.expandedWidthPx);
  useEffect(() => {
    if (model.expandedWidthPx !== lastPersistedRef.current) {
      lastPersistedRef.current = model.expandedWidthPx;
      persistWidth(model.expandedWidthPx);
    }
  }, [model.expandedWidthPx]);

  return [model, dispatch];
}
